import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Tooltip,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogTitle,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import {
  accountsForType,
  activeAccountConflict,
  deleteAccount,
  disconnectAccount,
  fetchSetupAccountRefreshTokens,
  updateAccountName,
} from "../../helper/channelIntegrattion/amazonAccountStore";

const STATUS_STYLE = {
  active: {
    label: "Active",
    color: "#1B7F3A",
    background: "#EDF7ED",
    icon: <CheckCircle sx={{ color: "#1B7F3A", fontSize: 22 }} />,
  },
  disabled: {
    label: "Disabled",
    color: "#5F6368",
    background: "#F5F5F5",
    icon: <RadioButtonUnchecked sx={{ color: "#9E9E9E", fontSize: 22 }} />,
  },
  expired: {
    label: "Expired",
    color: "#663C00",
    background: "#FFF4E5",
    icon: <ErrorOutline sx={{ color: "#ED6C02", fontSize: 22 }} />,
  },
};

const AccountNameEditor = ({ account, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(account.account_name || "");

  const startEdit = () => {
    setDraft(account.account_name || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    if (saving) return;
    setDraft(account.account_name || "");
    setEditing(false);
  };

  const saveEdit = async () => {
    if (saving) return;
    const nextName = draft.trim();
    if (!nextName || nextName === (account.account_name || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(nextName);
      setEditing(false);
    } catch (error) {
      toast.error(error?.message || "Unable to save the account name");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
        <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
          {account.account_name || "Unnamed account"}
        </Typography>
        <IconButton size="small" aria-label="Edit account name" onClick={startEdit}>
          <EditOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5} minWidth={0} flex={1}>
      <TextField
        size="small"
        value={draft}
        autoFocus
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") saveEdit();
          if (event.key === "Escape") cancelEdit();
        }}
      />
      <IconButton
        size="small"
        aria-label="Save account name"
        onClick={saveEdit}
        disabled={saving}
      >
        {saving ? (
          <CircularProgress size={18} />
        ) : (
          <Check sx={{ fontSize: 18, color: "#1B7F3A" }} />
        )}
      </IconButton>
      <IconButton
        size="small"
        aria-label="Cancel account name edit"
        onClick={cancelEdit}
        disabled={saving}
      >
        <Close sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

const statusStyle = (status) =>
  STATUS_STYLE[String(status || "").toLowerCase()] || {
    label: status || "Unknown",
    color: "#5F6368",
    background: "#F5F5F5",
    icon: <RadioButtonUnchecked sx={{ color: "#9E9E9E", fontSize: 22 }} />,
  };

const ConnectAmazonPortal = ({ portal, onAccountsChanged }) => {
  const { clientId } = useGlobalContext();
  const isSeller = portal === "seller";
  const [accountName, setAccountName] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [allTokens, setAllTokens] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const trimmedName = accountName.trim();

  useEffect(() => {
    let active = true;
    setAccountsLoading(true);
    setAccounts([]);
    setAllTokens([]);
    fetchSetupAccountRefreshTokens(clientId)
      .then((tokens) => {
        if (!active) return;
        setAllTokens(tokens);
        setAccounts(accountsForType(portal, tokens));
      })
      .finally(() => {
        if (active) setAccountsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId, portal]);

  const refreshAccounts = async () => {
    const tokens = await fetchSetupAccountRefreshTokens(clientId);
    setAllTokens(tokens);
    setAccounts(accountsForType(portal, tokens));
  };

  const openConsent = ({ name, tokenId = "" }) => {
    const params = new URLSearchParams({
      start: portal,
      accountName: name,
      clientId: clientId == null ? "" : String(clientId),
      tokenId,
    });
    window.open(
      `${window.location.origin}/onboarding-amazon?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const connectMessage = activeAccountConflict(allTokens, { tokenType: portal });

  const handleConnect = () => {
    if (!trimmedName || connectMessage) return;
    openConsent({ name: trimmedName });
  };

  const handleReconnect = (account) => {
    if (activeAccountConflict(allTokens, { tokenType: portal, tokenId: account.token_id })) {
      return;
    }
    openConsent({
      name: account.account_name || "",
      tokenId: account.token_id,
    });
  };

  const handleNameSave = async (account, nextName) => {
    await updateAccountName(clientId, account.token_id, nextName);
    await refreshAccounts();
  };

  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await disconnectAccount(clientId, disconnectTarget.token_id);
      setDisconnectTarget(null);
      await refreshAccounts();
      if (onAccountsChanged) await onAccountsChanged();
    } catch (error) {
      toast.error(error?.message || "Unable to disconnect the account");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAccount(clientId, deleteTarget.token_id);
      setDeleteTarget(null);
      await refreshAccounts();
      if (onAccountsChanged) await onAccountsChanged();
    } catch (error) {
      toast.error(error?.message || "Unable to delete the account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box flex={1} overflow="auto" px={3} py={3} display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        gap={1}
        alignItems="flex-start"
        sx={{ bgcolor: "#F7F6FF", borderRadius: "8px", px: 1.5, py: 1.25 }}
      >
        <InfoOutlined sx={{ fontSize: 18, color: "primary.main", mt: "2px" }} />
        <Typography variant="body2" color="text.secondary">
          Before clicking Connect, make sure you are logged in to Amazon{" "}
          {isSeller ? "Seller" : "Vendor"} Central and have selected the account
          you want to connect.
        </Typography>
      </Box>
      <TextField
        size="small"
        label="Account name"
        value={accountName}
        onChange={(event) => setAccountName(event.target.value)}
        required
        helperText="Enter a name, then connect. This name is saved with the account."
        onKeyDown={(event) => {
          if (event.key === "Enter") handleConnect();
        }}
      />
      <Box>
        <Tooltip title={connectMessage || ""}>
          <span>
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={!trimmedName || Boolean(connectMessage)}
            >
              {isSeller ? "Connect Seller Central" : "Connect Vendor Central"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="subtitle2">
        Connected accounts{accountsLoading ? "" : ` (${accounts.length})`}
      </Typography>

      {accountsLoading ? (
        [0, 1].map((item) => (
          <Box
            key={item}
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              p: 2,
              display: "flex",
              gap: 1.5,
            }}
          >
            <Skeleton variant="circular" width={22} height={22} />
            <Box flex={1}>
              <Skeleton variant="text" width="55%" height={28} />
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="text" width="80%" />
            </Box>
          </Box>
        ))
      ) : accounts.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed #E0E0E0",
            borderRadius: "8px",
            px: 2,
            py: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No accounts connected yet.
          </Typography>
        </Box>
      ) : (
        accounts.map((account) => {
          const status = statusStyle(account.status);
          return (
            <Box
              key={account.token_id}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                background: "#fff",
              }}
            >
              <Box sx={{ pt: 0.25 }}>{status.icon}</Box>
              <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={0.75}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <AccountNameEditor
                    account={account}
                    onSave={(nextName) => handleNameSave(account, nextName)}
                  />
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: "999px",
                      background: status.background,
                      color: status.color,
                      fontSize: 12,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {status.label}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {account.token_type === "vendor" ? "Vendor" : "Seller"}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  Selling partner id: {account.selling_partner_id || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  Token id: {account.token_id}
                </Typography>
                <Box display="flex" gap={1}>
                  {String(account.status || "").toLowerCase() === "active" ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setDisconnectTarget(account)}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Tooltip
                      title={
                        activeAccountConflict(allTokens, {
                          tokenType: portal,
                          tokenId: account.token_id,
                        }) || ""
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleReconnect(account)}
                          disabled={Boolean(
                            activeAccountConflict(allTokens, {
                              tokenType: portal,
                              tokenId: account.token_id,
                            })
                          )}
                        >
                          Reconnect
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteTarget(account)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
      <Dialog
        open={Boolean(disconnectTarget)}
        onClose={() => {
          if (!disconnecting) setDisconnectTarget(null);
        }}
      >
        <DialogTitle>Disconnect account</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Disconnect {disconnectTarget?.account_name || "this account"}? Its status
            will be set to disabled. You can reconnect it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectTarget(null)} disabled={disconnecting}>
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDisconnect}
            disabled={disconnecting}
          >
            Yes, disconnect
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
      >
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete {deleteTarget?.account_name || "this account"}? It will be
            removed from this list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectAmazonPortal;
